import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Mail, Eye, Globe, LogOut, Plus, Trash2, Pencil,
  BarChart3, Copy, Check, Clock, MonitorSmartphone,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e, f = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : f;
};

const roleBadge = {
  superadmin: "bg-emerald-900 text-base-bg",
  admin: "bg-sand text-emerald-950",
  member: "bg-emerald-900/10 text-emerald-900",
};

const inputCls =
  "w-full bg-transparent border border-emerald-900/20 px-3 py-2.5 text-emerald-900 placeholder:text-emerald-900/30 focus:border-emerald-900 focus:outline-none transition-colors rounded-none";

function StatCard({ icon: Icon, label, value, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.07, duration: 0.5 }}
      className="border border-emerald-900/15 bg-white/50 p-6"
      data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between text-emerald-900/50">
        <span className="text-[0.7rem] uppercase tracking-[0.16em]">{label}</span>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-4 font-serif text-4xl text-emerald-900 leading-none">{value}</p>
    </motion.div>
  );
}

function UserForm({ initial, onSubmit, submitLabel }) {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [role, setRole] = useState(initial?.role || "member");
  const [password, setPassword] = useState("");
  const [active, setActive] = useState(initial ? initial.active !== false : true);
  const [busy, setBusy] = useState(false);
  const editing = !!initial;

  const genPassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let p = "";
    for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setPassword(p);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({ name, email, role, password, active });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="text-[0.7rem] uppercase tracking-[0.14em] text-emerald-900/50">Name</label>
        <input data-testid="user-form-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
      </div>
      <div>
        <label className="text-[0.7rem] uppercase tracking-[0.14em] text-emerald-900/50">Email</label>
        <input data-testid="user-form-email" type="email" disabled={editing} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@tiltventures.ie" className={`${inputCls} ${editing ? "opacity-60" : ""}`} />
      </div>
      <div>
        <label className="text-[0.7rem] uppercase tracking-[0.14em] text-emerald-900/50">Role</label>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="user-form-role" className="rounded-none border-emerald-900/20"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="member">Member — analytics only</SelectItem>
            <SelectItem value="admin">Admin — analytics</SelectItem>
            <SelectItem value="superadmin">Super Admin — full control</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="flex items-center justify-between text-[0.7rem] uppercase tracking-[0.14em] text-emerald-900/50">
          <span>{editing ? "New password (leave blank to keep current)" : "Password"}</span>
          <button type="button" data-testid="user-form-generate" onClick={genPassword} className="text-emerald-900 normal-case tracking-normal text-xs underline hover:opacity-70">Generate</button>
        </label>
        <input data-testid="user-form-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={editing ? "Min 8 characters" : "Leave blank to auto-generate a secure one"} className={inputCls} />
      </div>
      {editing && (
        <div className="flex items-center justify-between border border-emerald-900/15 px-3 py-3">
          <div>
            <p className="text-sm text-emerald-900 font-medium">Account active</p>
            <p className="text-xs text-emerald-900/50">Disable to block sign-in without deleting.</p>
          </div>
          <Switch data-testid="user-form-active" checked={active} onCheckedChange={setActive} />
        </div>
      )}
      <button
        data-testid="user-form-submit"
        onClick={submit}
        disabled={busy}
        className="w-full bg-emerald-900 text-base-bg py-3 text-sm uppercase tracking-[0.16em] disabled:opacity-60"
      >
        {busy ? "Saving..." : submitLabel}
      </button>
    </div>
  );
}

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [visits, setVisits] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ov, us] = await Promise.all([
        axios.get(`${API}/admin/overview`),
        axios.get(`${API}/admin/users`),
      ]);
      setOverview(ov.data);
      setUsers(us.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) navigate("/login");
    }
  }, [navigate]);

  const loadContacts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contact`);
      setContacts(data);
    } catch (e) {
      console.error("Failed to load propositions:", e);
      toast.error("Couldn't load propositions. Please refresh.");
    }
  }, []);

  const loadVisits = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/visits`);
      setVisits(data);
    } catch (e) {
      console.error("Failed to load visits:", e);
      toast.error("Couldn't load visitor data. Please refresh.");
    }
  }, []);

  useEffect(() => { load(); loadContacts(); loadVisits(); }, [load, loadContacts, loadVisits]);

  const createUser = async ({ name, email, role, password }) => {
    try {
      const { data } = await axios.post(`${API}/admin/users`, { email, name, role, password });
      if (data.temp_password) {
        navigator.clipboard?.writeText(data.temp_password);
        toast.success(`User created. Temporary password (copied): ${data.temp_password}`, { duration: 9000 });
      } else {
        toast.success("User created.");
      }
      setAddOpen(false);
      load();
    } catch (e) { toast.error(err(e, "Could not create user")); }
  };

  const updateUser = async ({ name, role, password, active }) => {
    try {
      const body = { name, role, active };
      if (password) body.password = password;
      await axios.patch(`${API}/admin/users/${editUser.id}`, body);
      toast.success("User updated.");
      setEditUser(null);
      load();
    } catch (e) { toast.error(err(e, "Could not update user")); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      toast.success("User removed.");
      load();
    } catch (e) { toast.error(err(e, "Could not remove user")); }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Delete proposition from ${c.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/contacts/${c.id}`);
      toast.success("Proposition deleted.");
      loadContacts();
    } catch (e) { toast.error(err(e, "Could not delete")); }
  };

  const copyEmail = (email) => {
    navigator.clipboard?.writeText(email);
    setCopied(email);
    setTimeout(() => setCopied(null), 1500);
  };

  const doLogout = () => { logout(); navigate("/"); };

  const unreadCount = contacts.filter((c) => !c.read).length;

  const markPropositionsRead = async () => {
    try {
      await axios.post(`${API}/admin/contacts/read-all`);
      setContacts((prev) => prev.map((c) => ({ ...c, read: true })));
    } catch (e) {
      console.error("Failed to mark propositions read:", e);
    }
  };

  const selectTab = (id) => {
    setTab(id);
    if (id === "propositions" && unreadCount > 0) markPropositionsRead();
  };

  const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "visitors", label: "Visitors", icon: Globe },
    { id: "propositions", label: "Propositions", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-base-bg" data-testid="superadmin-page">
      {/* Top bar */}
      <header className="border-b border-emerald-900/15 bg-emerald-950 text-base-bg sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-sand" />
            <span className="font-serif text-2xl">TILT</span>
            <span className="text-[0.7rem] uppercase tracking-[0.2em] text-base-bg/60 border-l border-base-bg/20 pl-3">Super Admin Panel</span>
          </div>
          <div className="flex items-center gap-6">
            <button data-testid="admin-goto-analytics" onClick={() => navigate("/analytics")} className="hidden sm:inline text-[0.72rem] uppercase tracking-[0.14em] text-base-bg/70 hover:text-base-bg transition-colors">← Admin Panel</button>
            <span className="hidden md:block text-sm text-base-bg/60">{user?.email}</span>
            <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.14em] hover:opacity-70 transition-opacity">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-12">
        <div className="mb-10">
          <p className="text-[0.72rem] uppercase tracking-[0.22em] text-emerald-900/50">Control panel</p>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-emerald-900 leading-none">Manage TILT access</h1>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-emerald-900/15 mb-10 overflow-x-auto no-scrollbar -mx-6 px-6 md:mx-0 md:px-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => selectTab(t.id)}
              className={`shrink-0 whitespace-nowrap inline-flex items-center gap-2 px-4 md:px-5 py-3 text-sm uppercase tracking-[0.12em] border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-emerald-900 text-emerald-900" : "border-transparent text-emerald-900/45 hover:text-emerald-900/70"
              }`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "propositions" && unreadCount > 0 && (
                <span
                  data-testid="propositions-unread-badge"
                  className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-600 text-white text-[0.65rem] font-semibold tracking-normal"
                >
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview */}
        {tab === "overview" && overview && (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <StatCard i={0} icon={Users} label="Team Users" value={overview.users} />
              <StatCard i={1} icon={Eye} label="Total Visits" value={overview.visits} />
              <StatCard i={2} icon={Mail} label="Propositions" value={overview.contacts} />
              <StatCard i={3} icon={Globe} label="Unique Visitors" value={overview.unique_visitors} />
            </div>
            <div className="mt-8 border border-emerald-900/15 bg-white/50 p-6 md:p-8">
              <h2 className="font-serif text-2xl text-emerald-900 mb-6">Recently added</h2>
              <div className="divide-y divide-emerald-900/10">
                {overview.recent_users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between py-3.5">
                    <div>
                      <p className="text-emerald-900 font-medium">{u.name}</p>
                      <p className="text-sm text-emerald-900/50">{u.email}</p>
                    </div>
                    <span className={`px-3 py-1 text-[0.68rem] uppercase tracking-[0.12em] ${roleBadge[u.role] || roleBadge.member}`}>{u.role}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users */}
        {tab === "users" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="text-emerald-900/60 text-sm">{users.length} team member{users.length !== 1 ? "s" : ""}</p>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <button data-testid="admin-add-user" className="inline-flex items-center gap-2 bg-emerald-900 text-base-bg px-5 py-2.5 text-sm uppercase tracking-[0.14em]">
                    <Plus className="h-4 w-4" /> Add user
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-none border-emerald-900/20 bg-base-bg">
                  <DialogHeader><DialogTitle className="font-serif text-2xl text-emerald-900">Add team member</DialogTitle></DialogHeader>
                  <UserForm onSubmit={createUser} submitLabel="Create user" />
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-emerald-900/15 bg-white/50 overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-users-table">
                <thead>
                  <tr className="text-[0.68rem] uppercase tracking-[0.14em] text-emerald-900/50 border-b border-emerald-900/10">
                    <th className="px-5 py-4 font-medium">Name</th>
                    <th className="px-5 py-4 font-medium">Email</th>
                    <th className="px-5 py-4 font-medium">Role</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 font-medium">Last login</th>
                    <th className="px-5 py-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-900/8">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="text-sm">
                      <td className="px-5 py-4 text-emerald-900 font-medium">{u.name}</td>
                      <td className="px-5 py-4 text-emerald-900/70">
                        <button onClick={() => copyEmail(u.email)} className="inline-flex items-center gap-2 hover:text-emerald-900">
                          {u.email}
                          {copied === u.email ? <Check className="h-3.5 w-3.5 text-emerald-700" /> : <Copy className="h-3.5 w-3.5 opacity-40" />}
                        </button>
                      </td>
                      <td className="px-5 py-4"><span className={`px-2.5 py-1 text-[0.66rem] uppercase tracking-[0.1em] ${roleBadge[u.role] || roleBadge.member}`}>{u.role}</span></td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 text-[0.66rem] uppercase tracking-[0.1em] ${u.active === false ? "bg-red-700/10 text-red-700" : "bg-emerald-900/10 text-emerald-900"}`}>
                          {u.active === false ? "Disabled" : "Active"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-emerald-900/60">{fmtTime(u.last_login)}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`edit-user-${u.email}`} onClick={() => setEditUser(u)} className="p-2 text-emerald-900/60 hover:text-emerald-900 hover:bg-emerald-900/5" title="Edit"><Pencil className="h-4 w-4" /></button>
                          <button data-testid={`delete-user-${u.email}`} onClick={() => removeUser(u)} className="p-2 text-red-700/70 hover:text-red-700 hover:bg-red-700/5" title="Remove"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
              <DialogContent className="rounded-none border-emerald-900/20 bg-base-bg">
                <DialogHeader><DialogTitle className="font-serif text-2xl text-emerald-900">Edit {editUser?.name}</DialogTitle></DialogHeader>
                {editUser && <UserForm initial={editUser} onSubmit={updateUser} submitLabel="Save changes" />}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Visitors */}
        {tab === "visitors" && (
          <div data-testid="admin-visitors">
            <p className="text-emerald-900/60 text-sm mb-6">Most recent {visits.length} visits — where people come from, their device and IP.</p>
            <div className="border border-emerald-900/15 bg-white/50 overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-visitors-table">
                <thead>
                  <tr className="text-[0.68rem] uppercase tracking-[0.14em] text-emerald-900/50 border-b border-emerald-900/10">
                    <th className="px-5 py-4 font-medium"><Globe className="inline h-3.5 w-3.5 mr-1" />Source</th>
                    <th className="px-5 py-4 font-medium">IP address</th>
                    <th className="px-5 py-4 font-medium"><MonitorSmartphone className="inline h-3.5 w-3.5 mr-1" />Device</th>
                    <th className="px-5 py-4 font-medium">Browser</th>
                    <th className="px-5 py-4 font-medium">Page</th>
                    <th className="px-5 py-4 font-medium"><Clock className="inline h-3.5 w-3.5 mr-1" />When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-900/8">
                  {visits.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-8 text-emerald-900/50 text-sm">No visits recorded yet.</td></tr>
                  )}
                  {visits.map((v) => (
                    <tr key={v.id} className="text-sm">
                      <td className="px-5 py-4 text-emerald-900 font-medium">{v.source}</td>
                      <td className="px-5 py-4 text-emerald-900/60 font-mono text-xs">{v.ip}</td>
                      <td className="px-5 py-4 text-emerald-900/70">
                        {v.device} <span className="text-emerald-900/40">· {v.type}</span>
                      </td>
                      <td className="px-5 py-4 text-emerald-900/70">{v.browser}</td>
                      <td className="px-5 py-4 text-emerald-900/60">{v.path}</td>
                      <td className="px-5 py-4 text-emerald-900/60 whitespace-nowrap">{fmtTime(v.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Propositions */}
        {tab === "propositions" && (
          <div className="space-y-4" data-testid="admin-propositions">
            {contacts.length === 0 && <p className="text-emerald-900/50">No propositions received yet.</p>}
            {contacts.map((c) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-emerald-900/15 bg-white/50 p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-xl text-emerald-900">{c.name}{c.company ? <span className="text-emerald-900/40 text-base"> · {c.company}</span> : null}</p>
                    <a href={`mailto:${c.email}`} className="text-sm text-emerald-900/60 hover:text-emerald-900">{c.email}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-emerald-900/40 whitespace-nowrap">{fmtTime(c.created_at)}</span>
                    <button data-testid={`delete-contact-${c.id}`} onClick={() => removeContact(c)} className="p-2 text-red-700/70 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <p className="mt-4 text-base-text/80 leading-relaxed border-l-2 border-emerald-900/20 pl-4">{c.message}</p>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
